/**
 * The wire. Protocol v3 — see `docs/PROTOCOL-V3.md`, which is the frozen contract this file
 * implements; if the two ever disagree, the document is wrong and this file is right, but they
 * are meant to be checked against each other whenever either moves.
 *
 * v3 is the MMORPG pivot. Out: hitscan and shot events, ammo, weapon swap, aiming, prone, lag
 * compensation, the raid clock, weather, extraction, the dropship. In: a body yaw, a selected
 * target on every input command, six ability slots, a level, a max HP, an animation state and a
 * packed appearance word — plus u16 list lengths everywhere, because a city room holds 500
 * players and a u8 count tops out at 255.
 */

import { ByteReader, ByteWriter } from './bytes.js';
import { MAX_INPUTS_PER_MESSAGE } from './constants.js';
import { q16, uq16, type Vec3 } from './math.js';
import type { InputCmd } from './sim.js';
import type { ItemStack } from './items.js';

export const C2S = { Hello: 1, Input: 2, Ping: 3, Chat: 4, Interact: 5 } as const;

/**
 * Interact actions (C2S.Interact).
 *
 * `Talk` is the verb, not its consequence: talking to Isolde is what leaves The Waking, but
 * `NpcDef` is a general facility and the next person you meet will only sell you something.
 * A new member here is not a codec change — the message already carries a `u8` action, a `u16`
 * target and a `u32` item uid — so no wire format moved to add the five v3 verbs.
 *
 * 5 was `Extract` and is deliberately not reused: extraction is gone, and a v2 client whose
 * "leave the raid" keypress silently became "equip" would be worse than one that did nothing.
 */
export const InteractAction = {
  Open: 1,
  Take: 2,
  Use: 3,
  Drop: 4,
  // 5 reserved (was Extract)
  Revive: 6,
  Talk: 7,
  Equip: 8,
  Unequip: 9,
  /** Destroy an item outright. The bag's right-click "Drop" — it does not become a loot bag. */
  Destroy: 10,
  /** Repair one item, or every equipped item when `itemUid` is 0, at a vendor. */
  Repair: 11,
  /** Dead: give up the body and respawn at the nearest graveyard. */
  Release: 12,
} as const;
export interface InteractMsg {
  type: typeof C2S.Interact;
  action: number;
  /** Container / loot bag / NPC id, or 0. */
  targetId: number;
  /** Item stack uid for Take/Use/Drop/Equip/Unequip/Destroy/Repair, or 0. */
  itemUid: number;
}

/** Non-player entities (Hymns, loot bags, deployables) share one snapshot path. */
export const EntityKind = {
  Chorister: 1,
  Cantor: 2,
  Sexton: 3,
  Verger: 4,
  ChoirAnchor: 5,
  LootBag: 20,
  Bulwark: 30,
  Sentry: 31,
  Drone: 32,
  Beacon: 33,
  Fabricator: 34,
} as const;

/**
 * Reference max HP per entity kind. The server AI tuning is authoritative and `EntitySnap.maxHp`
 * now carries the real number, so this survives only as the client's fallback for an entity whose
 * first snapshot has not arrived yet.
 */
export const ENTITY_MAX_HP: Record<number, number> = {
  1: 80,
  2: 60,
  3: 320,
  4: 2400,
  5: 150,
  20: 1,
  30: 400,
  31: 120,
  32: 1,
  33: 1,
  34: 1,
};
export const EntityState = {
  Idle: 0,
  Patrol: 1,
  Alert: 2,
  Attack: 3,
  Charging: 4,
  Stagger: 5,
  Dead: 6,
} as const;
export interface EntitySnap {
  id: number;
  kind: number;
  pos: Vec3;
  yaw: number;
  hp: number;
  /** Level-scaled, so a bar can be drawn without a per-kind table. */
  maxHp: number;
  state: number;
  /** Player id this entity is focused on, or 0. */
  targetId: number;
  level: number;
  /**
   * Milliseconds left on a cast, and **only present on the wire while `state === Charging`**.
   *
   * A Cantor winds up for about a second every few seconds. Paying two bytes per entity per tick
   * for the other 80% of the time is the trade v2 already refused once (for the mantle tail), so
   * this uses the same conditional-tail technique on the field that actually earns it. Absent
   * decodes as 0.
   */
  castEndsAt?: number;
}

/**
 * @deprecated Vestigial. The raid clock is gone; every room sits in `Live` forever. The server
 * keeps one number for the admin dashboard and `S2C.RaidState` no longer exists.
 */
export const RaidPhase = { Lobby: 0, Live: 1, Dimming: 2, Ended: 3 } as const;

export const S2C = {
  Welcome: 1,
  Snapshot: 2,
  PlayerJoined: 3,
  PlayerLeft: 4,
  Pong: 5,
  Chat: 6,
  Kick: 7,
  Events: 8,
  Score: 9,
  Identity: 10,
  // 11 reserved (was RaidState: phase clock, weather, extracts — all gone)
  Loot: 12,
  Inventory: 13,
  // 14 reserved (was Extracted)
  Progress: 15,
  QuestState: 16,
  // 17 reserved (was Ammo)
  Transfer: 18,
  /**
   * Added 2026-09-06 (Phase 2d, party). A **minor bump**: `PROTOCOL_VERSION` stays 3 because
   * adding a server→client message breaks nothing — a client that does not know 19 drops it in
   * `decodeServer`'s `default`, and a server that never sends one is a server with no parties.
   * Recorded in `docs/PROTOCOL-V3.md` §3.11.
   */
  PartyState: 19,
} as const;

/** The quest area, flattened for the wire (see `AreaDef`/`quests.ts`). */
export interface QuestAreaSnap {
  x: number;
  y: number;
  z: number;
  radius: number;
  name: string;
}
/**
 * The quest chain's current rung. Sent on every meaningful change (start, progress, breather,
 * timer pause/resume) and at most ~1 Hz while a HOLD clock is running — the client counts down
 * locally from the last `timeLeftMs`. Also pushed to a late joiner right after its Welcome.
 */
export interface QuestStateMsg {
  type: typeof S2C.QuestState;
  /** 0-based rung of this chain. */
  chainIndex: number;
  questId: string;
  /** `QuestKind` (quests.ts). */
  kind: number;
  title: string;
  objective: string;
  progress: number;
  goal: number;
  area?: QuestAreaSnap;
  /** HOLD / timed quests: ms of quest clock left. Absent when the quest is untimed. */
  timeLeftMs?: number;
  /** Present only during the breather between quests; the quest fields then describe the one just finished. */
  breatherMs?: number;
  /** Who handed this quest out (`VoiceId`), and the two lines they said. Absent on procedural quests. */
  voice?: string;
  brief?: [string, string];
  /**
   * `NpcDef.id` on the current map: this rung is done and is waiting to be handed in to that
   * person. Present only while the chain is held — it clears the moment `InteractAction.Talk`
   * turns it in. Only the id crosses the wire; the client resolves the name and the marker
   * position out of `MapDef.npcs`, which it already holds.
   */
  handInNpcId?: number;
}

export interface IdentityMsg {
  type: typeof S2C.Identity;
  playerId: number;
  name: string;
  /** 'player' | 'founder' | 'admin' | 'owner' */
  role: string;
  wick: number;
  level: number;
}

/**
 * Zone hand-off. The player has walked into an exit region and now stands in another map, in the
 * same process, on the same socket. A `Welcome` for the new room follows immediately, so the
 * roster path needs no special case.
 *
 * The client swaps the active `MapDef` (both zones are prefetched at boot), hard-sets the
 * predictor to `spawn`/`yaw`, clears every remote player and entity, and clears its target. There
 * is no reconnect and no load screen — see `docs/PROTOCOL-V3.md` §3.10.
 */
export interface TransferMsg {
  type: typeof S2C.Transfer;
  mapId: string;
  spawn: Vec3;
  yaw: number;
}

/** Flags on a `PartyMemberSnap`. */
export const PartyFlags = {
  /** This row is the recipient's own. The frames draw the viewer differently, or not at all. */
  Self: 1,
  Dead: 2,
  Downed: 4,
  /**
   * Near enough that the vitals on this row are live — the same range the snapshot keeps a body
   * at (`PARTY_FRAME_RANGE_M`). Cleared, and the frame drawn pale, when the numbers are the last
   * ones the server saw rather than this tick's. Always set on the recipient's own row.
   */
  InRange: 8,
} as const;

/**
 * One row of `S2C.PartyState`.
 *
 * The vitals are on this message rather than read off the snapshot on purpose: a party member
 * can be eighty metres away, past the relevance cut, and a healer whose frames go blank at the
 * exact moment somebody runs out of line of sight has no party UI at all. Five members at
 * ~20 bytes, sent on change and otherwise at 2 Hz, is nothing next to the snapshot.
 */
export interface PartyMemberSnap {
  /** Room-local player id. */
  id: number;
  name: string;
  level: number;
  /** Packed appearance (§4) — the frames draw the portrait from race + Calling. */
  look: number;
  hp: number;
  maxHp: number;
  /** `PartyFlags`. */
  flags: number;
}

/**
 * Who the recipient is grouped with. Sent on every membership or leadership change and
 * otherwise at a low rate for the vitals; an empty `members` list means "you are not in a
 * party", which is how a leave, a kick and a disband all arrive.
 */
export interface PartyStateMsg {
  type: typeof S2C.PartyState;
  /** Player id of the leader, or 0 when there is no party. */
  leaderId: number;
  members: PartyMemberSnap[];
}

/** Contents of a container the local player opened, or a loot bag they are looking at. */
export interface LootMsg {
  type: typeof S2C.Loot;
  containerId: number;
  items: ItemStack[];
}
/** Local player's carried inventory (full replace). Stash is fetched over HTTP. */
export interface InventoryMsg {
  type: typeof S2C.Inventory;
  items: ItemStack[];
}
/** Progression snapshot for the local player: sent after Welcome and whenever it changes. */
export interface ProgressMsg {
  type: typeof S2C.Progress;
  xp: number;
  level: number;
  skillPoints: number;
  calling: number;
  /** Remaining cooldown per ability slot, ms (0 = ready). Six since v3. */
  cooldownsMs: [number, number, number, number, number, number];
  /** Remaining global cooldown, ms. */
  gcdMs: number;
  /** Vanguard rage / Warden mana. 0 for a calling with no resource. */
  resource: number;
  resourceMax: number;
  /**
   * v3.1. Milliseconds left on the in-progress cast, 0 when not casting.
   *
   * The local player's own cast bar has to come from somewhere, and it cannot come from
   * `SnapFlags.Casting` — a flag says *that* you are casting, not how much of it is left, and a
   * bar that fills by guesswork drifts against the server that will actually decide whether the
   * spell landed. `castSlot` names which of the six is filling, so the bar can be drawn under
   * that slot and labelled with the ability's name.
   */
  castMs: number;
  /** v3.1. Which slot (0..5) `castMs` belongs to. Meaningless when `castMs` is 0. */
  castSlot: number;
}

/** Discrete gameplay events, batched per tick in an Events message. */
export const Ev = {
  // 1 reserved (was Shot — hitscan is gone)
  Damage: 2,
  Death: 3,
  Respawn: 4,
  Ability: 5,
  Downed: 6,
  Revived: 7,
  MarkUnlocked: 8,
  ContractComplete: 9,
  QuestComplete: 10,
  Heal: 11,
  /**
   * v3.1. Somebody **else** started a cast. The local player's own bar rides `S2C.Progress`
   * (`castMs`/`castSlot`); this is the one that puts a bar over a stranger's nameplate and under
   * the target frame, which is the only way a party learns to interrupt anything.
   *
   * `castMs` is the **full** cast length rather than the remaining time, because the event is
   * sent once at the start and the client animates from it. A cast that is interrupted arrives as
   * a second `Cast` with `castMs: 0`.
   */
  Cast: 12,
} as const;

/** A quest just completed; the reward has already been applied server-side. */
export interface QuestCompleteEvent {
  kind: typeof Ev.QuestComplete;
  questId: string;
  chainIndex: number;
  wick: number;
  xp: number;
  /** `VoiceId` of whoever is about to say `line`, and the resolution line itself. May be empty. */
  voice: string;
  line: string;
}

export interface AbilityEvent {
  kind: typeof Ev.Ability;
  playerId: number;
  /** Ability slot 0..5 on the player's Calling. */
  slot: number;
  pos: Vec3;
  /** The ability's target (player or entity), or 0. */
  targetId: number;
}
export interface DownedEvent {
  kind: typeof Ev.Downed;
  id: number;
}
export interface RevivedEvent {
  kind: typeof Ev.Revived;
  id: number;
  byId: number;
}
export interface ContractCompleteEvent {
  kind: typeof Ev.ContractComplete;
  playerId: number;
  contractId: string;
}
export interface MarkUnlockedEvent {
  kind: typeof Ev.MarkUnlocked;
  playerId: number;
  markId: string;
  tier: number;
}

/**
 * `amount` and `hpAfter` are u16 since v3: hp is a u16 now, a level-10 Sexton has more than 255
 * of it, and a crit for 300 must not wrap round to 44.
 */
export interface DamageEvent {
  kind: typeof Ev.Damage;
  targetId: number;
  attackerId: number;
  amount: number;
  hpAfter: number;
}
/** The healer's counterpart to `DamageEvent`; the combat log and floating numbers read both. */
export interface HealEvent {
  kind: typeof Ev.Heal;
  targetId: number;
  healerId: number;
  amount: number;
  hpAfter: number;
}
/**
 * v3.1. A player began (or, with `castMs: 0`, abandoned) a cast. See `Ev.Cast`.
 */
export interface CastEvent {
  kind: typeof Ev.Cast;
  casterId: number;
  /** Ability slot 0..5. */
  slot: number;
  /** Full cast length in ms; 0 means the cast was interrupted and the bar should clear. */
  castMs: number;
  targetId: number;
}
export interface DeathEvent {
  kind: typeof Ev.Death;
  victimId: number;
  killerId: number;
  /** What did it: an ability id or 0 for the environment. Kept a u8 as it always was. */
  weaponId: number;
}
export interface RespawnEvent {
  kind: typeof Ev.Respawn;
  id: number;
  pos: Vec3;
}
export type GameEvent =
  | DamageEvent
  | HealEvent
  | CastEvent
  | DeathEvent
  | RespawnEvent
  | AbilityEvent
  | DownedEvent
  | RevivedEvent
  | MarkUnlockedEvent
  | ContractCompleteEvent
  | QuestCompleteEvent;

export interface EventsMsg {
  type: typeof S2C.Events;
  tick: number;
  events: GameEvent[];
}
export interface ScoreEntry {
  id: number;
  kills: number;
  deaths: number;
}
export interface ScoreMsg {
  type: typeof S2C.Score;
  entries: ScoreEntry[];
}

export interface HelloMsg {
  type: typeof C2S.Hello;
  protocol: number;
  name: string;
  token: string;
}
export interface InputMsg {
  type: typeof C2S.Input;
  cmds: InputCmd[];
}
export interface PingMsg {
  type: typeof C2S.Ping;
  t: number;
}
export interface ChatC2S {
  type: typeof C2S.Chat;
  text: string;
}
export type ClientMessage = HelloMsg | InputMsg | PingMsg | ChatC2S | InteractMsg;

/**
 * 36 bytes, fixed, no optional tail. Everything a body needs to be drawn and read at a glance,
 * and nothing a client can work out for itself.
 *
 * What left in v3 and must not come back: `pitch` (the camera is the client's), `weapon`/`ammo`
 * (there are no guns), `stance`/`stanceTicks`/`regenTicks`/`groundTicks`/`speedMult`/`staminaMax`
 * (predicted locally; six bytes x 30 Hz x 500 players is not defensible for integers that
 * converge on their own inside a second), and the whole mantle tail (there is no mantling).
 */
export interface PlayerSnap {
  id: number;
  pos: Vec3;
  vel: Vec3;
  /** Body yaw. Quantised to a u8 over 2pi (~0.0123 rad) — see the note in the doc §3.1. */
  yaw: number;
  /** `AnimState`. Chosen server-side; the client blends between them as it likes. */
  anim: number;
  hp: number;
  maxHp: number;
  flags: number;
  /** Packed appearance; see `packLook`. */
  look: number;
  level: number;
  /** Who this player has selected, or 0. */
  targetId: number;
  /** 0..100, whole units. */
  stamina: number;
}

/**
 * What a body is doing. One value per player per snapshot, because the client no longer has the
 * state to infer it: stance, ground ticks and the firing flag all left the wire, and guessing
 * "running" from velocity alone put every knocked-back corpse into a sprint.
 *
 * Crouching is deliberately **not** in here — a crouching player can also be walking. It is
 * `SnapFlags.Crouching`, which the client combines with the anim.
 */
export const AnimState = {
  Idle: 0,
  Walk: 1,
  Run: 2,
  Jump: 3,
  Fall: 4,
  Sit: 5,
  Attack: 6,
  Cast: 7,
  Hit: 8,
  Dead: 9,
  Downed: 10,
  Emote: 11,
} as const;

export const SnapFlags = {
  Grounded: 1,
  Dead: 2,
  Downed: 4,
  Casting: 8,
  InCombat: 16,
  Sitting: 32,
  Sprinting: 64,
  Winded: 128,
  Crouching: 256,
  /**
   * v3.2. Something this player is wearing is at 0 durability. One bit rather than a per-slot
   * mask: the client only needs to know *that* the body should read as ruined, and the greying is
   * a whole-body tint in `render/crowd.ts`, not a per-piece material.
   */
  Broken: 512,
} as const;

// ---------------------------------------------------------------------------------------------
// look: packed appearance
// ---------------------------------------------------------------------------------------------

/**
 * The bit layout moved to `./look.js` when character creation and gear became two concurrent
 * owners of the same word (see that file's header). It is re-exported from here unchanged, so
 * every existing `import { packLook, Race } from './protocol.js'` keeps compiling and there is
 * still exactly one definition of the layout in the package.
 */
export {
  Race,
  PLAYABLE_RACES,
  isPlayableRace,
  packLook,
  unpackLook,
  withLook,
  raceOf,
  callingOf,
  type RaceId,
  type LookParts,
} from './look.js';

export interface WelcomeMsg {
  type: typeof S2C.Welcome;
  playerId: number;
  tick: number;
  tickRate: number;
  mapId: string;
  players: RosterEntry[];
}

/**
 * A player on the Welcome roster (and in PlayerJoined). `cosmetics` is the packed armour word
 * from `items.packCosmetics`: three bits per armour slot holding `rarity + 1`, 0 for an empty
 * slot. It stays for the hub's roster tiles; live appearance comes from `PlayerSnap.look`, which
 * unlike this one changes mid-session when somebody equips something.
 */
export interface RosterEntry {
  id: number;
  name: string;
  /** Packed armour rarities; see `packCosmetics`/`unpackCosmetics` in items.ts. 0 = bare. */
  cosmetics: number;
}
export interface SnapshotMsg {
  type: typeof S2C.Snapshot;
  tick: number;
  serverTime: number;
  lastInputSeq: number;
  players: PlayerSnap[];
  entities: EntitySnap[];
}
export interface PlayerJoinedMsg {
  type: typeof S2C.PlayerJoined;
  id: number;
  name: string;
  /** See `RosterEntry.cosmetics`. */
  cosmetics: number;
}
export interface PlayerLeftMsg {
  type: typeof S2C.PlayerLeft;
  id: number;
}
export interface PongMsg {
  type: typeof S2C.Pong;
  t: number;
  serverTime: number;
}
export interface ChatS2C {
  type: typeof S2C.Chat;
  fromId: number;
  text: string;
}
export interface KickMsg {
  type: typeof S2C.Kick;
  reason: string;
}
export type ServerMessage =
  | WelcomeMsg
  | SnapshotMsg
  | PlayerJoinedMsg
  | PlayerLeftMsg
  | PongMsg
  | ChatS2C
  | KickMsg
  | EventsMsg
  | ScoreMsg
  | IdentityMsg
  | LootMsg
  | InventoryMsg
  | ProgressMsg
  | QuestStateMsg
  | TransferMsg
  | PartyStateMsg;

/**
 * v3.2. The stack list gained a trailing `durability` u8 (0..100). It is appended, not inserted,
 * and every other field kept its width and its order, so the only thing a v3.1 reader gets wrong
 * is that it stops one byte early per stack. Absent durability encodes as 100 — pristine — which
 * is exactly what an item written before the column existed means.
 */
function writeStacks(w: ByteWriter, items: ItemStack[]) {
  w.u16w(Math.min(items.length, 65535));
  for (const s of items.slice(0, 65535)) {
    w.u32w(s.uid)
      .strw(s.itemId)
      .u16w(s.qty)
      .u8w(s.location)
      .u8w(s.slot)
      .u8w(s.insured ? 1 : 0)
      .u8w(Math.max(0, Math.min(255, Math.round(s.durability ?? 100))));
  }
}
function readStacks(r: ByteReader): ItemStack[] {
  const n = r.u16r();
  const items: ItemStack[] = [];
  for (let i = 0; i < n; i++) {
    items.push({
      uid: r.u32r(),
      itemId: r.strr(),
      qty: r.u16r(),
      location: r.u8r() as ItemStack['location'],
      slot: r.u8r(),
      insured: r.u8r() === 1,
      durability: r.u8r(),
    });
  }
  return items;
}

const POS_SCALE = 100; // 1 cm precision
const VEL_SCALE = 200; // +/- 163 m/s in an int16
const ANG_SCALE = 10000; // ~0.0001 rad
const TAU = Math.PI * 2;

function writePos(w: ByteWriter, v: Vec3) {
  w.i32w(Math.round(v.x * POS_SCALE));
  w.i32w(Math.round(v.y * POS_SCALE));
  w.i32w(Math.round(v.z * POS_SCALE));
}
function readPos(r: ByteReader): Vec3 {
  return { x: r.i32r() / POS_SCALE, y: r.i32r() / POS_SCALE, z: r.i32r() / POS_SCALE };
}
function writeVel(w: ByteWriter, v: Vec3) {
  w.i16w(q16(v.x, VEL_SCALE));
  w.i16w(q16(v.y, VEL_SCALE));
  w.i16w(q16(v.z, VEL_SCALE));
}
function readVel(r: ByteReader): Vec3 {
  return {
    x: uq16(r.i16r(), VEL_SCALE),
    y: uq16(r.i16r(), VEL_SCALE),
    z: uq16(r.i16r(), VEL_SCALE),
  };
}

/**
 * Body yaw in one byte: 256 steps over a full turn, about 0.0123 rad (0.7 degrees).
 *
 * Coarse on purpose. A remote body's facing is a thing you read from ten metres away at a glance,
 * and the byte it saves is paid 30 times a second by every player in a city of 500. The local
 * player never reads its own yaw back out of a snapshot (see the doc §3.1), so the coarseness
 * never reaches the one place it would be felt.
 */
export function packYaw(yaw: number): number {
  const t = ((yaw % TAU) + TAU) % TAU;
  return Math.round((t / TAU) * 256) & 255;
}
export function unpackYaw(byte: number): number {
  return ((byte & 255) / 256) * TAU;
}

export function encodeClient(m: ClientMessage): Uint8Array {
  const w = new ByteWriter(64);
  w.u8w(m.type);
  switch (m.type) {
    case C2S.Hello:
      w.u16w(m.protocol).strw(m.name).strw(m.token);
      break;
    case C2S.Input:
      w.u8w(m.cmds.length);
      for (const c of m.cmds) {
        w.u32w(c.seq).u16w(Math.round(c.dt * 10000));
        w.i8w(Math.round(c.moveX * 127)).i8w(Math.round(c.moveY * 127));
        w.i32w(Math.round(c.yaw * ANG_SCALE));
        w.u16w(c.buttons & 0xffff);
        w.u16w(c.targetId & 0xffff);
      }
      break;
    case C2S.Ping:
      w.f64w(m.t);
      break;
    case C2S.Chat:
      w.strw(m.text);
      break;
    case C2S.Interact:
      w.u8w(m.action)
        .u16w(m.targetId)
        .u32w(m.itemUid >>> 0);
      break;
  }
  return w.bytes();
}

function decodeClientUnsafe(data: ArrayBuffer | Uint8Array): ClientMessage | null {
  const r = new ByteReader(data);
  if (r.remaining < 1) return null;
  const type = r.u8r();
  switch (type) {
    case C2S.Hello:
      return { type, protocol: r.u16r(), name: r.strr(), token: r.strr() };
    case C2S.Input: {
      const n = r.u8r();
      if (n > MAX_INPUTS_PER_MESSAGE * 4) return null;
      const cmds: InputCmd[] = [];
      for (let i = 0; i < n; i++) {
        cmds.push({
          seq: r.u32r(),
          dt: r.u16r() / 10000,
          moveX: r.i8r() / 127,
          moveY: r.i8r() / 127,
          yaw: r.i32r() / ANG_SCALE,
          buttons: r.u16r(),
          targetId: r.u16r(),
        });
      }
      return { type, cmds };
    }
    case C2S.Ping:
      return { type, t: r.f64r() };
    case C2S.Chat:
      return { type, text: r.strr() };
    case C2S.Interact:
      return { type, action: r.u8r(), targetId: r.u16r(), itemUid: r.u32r() };
    default:
      return null;
  }
}

export function encodeServer(m: ServerMessage): Uint8Array {
  const w = new ByteWriter(256);
  w.u8w(m.type);
  switch (m.type) {
    case S2C.Welcome:
      w.u16w(m.playerId).u32w(m.tick).u8w(m.tickRate).strw(m.mapId).u16w(m.players.length);
      for (const p of m.players)
        w.u16w(p.id)
          .strw(p.name)
          .u16w(p.cosmetics & 0xffff);
      break;
    case S2C.Snapshot:
      w.u32w(m.tick).f64w(m.serverTime).u32w(m.lastInputSeq).u16w(m.players.length);
      for (const p of m.players) {
        w.u16w(p.id);
        writePos(w, p.pos);
        writeVel(w, p.vel);
        w.u8w(packYaw(p.yaw))
          .u8w(p.anim & 0xff)
          .u16w(clampU16(p.hp))
          .u16w(clampU16(p.maxHp))
          .u16w(p.flags & 0xffff)
          .u32w(p.look >>> 0)
          .u8w(p.level & 0xff)
          .u16w(p.targetId & 0xffff)
          .u8w(Math.max(0, Math.min(255, Math.round(p.stamina))));
      }
      w.u16w(Math.min(m.entities.length, 65535));
      for (const e of m.entities.slice(0, 65535)) {
        w.u16w(e.id).u8w(e.kind);
        writePos(w, e.pos);
        w.i16w(q16(e.yaw, 10000))
          .u16w(clampU16(e.hp))
          .u16w(clampU16(e.maxHp))
          .u8w(e.state)
          .u16w(e.targetId)
          .u8w(e.level & 0xff);
        // Conditional tail: only a Cantor actually mid-wind-up pays for its cast bar.
        if (e.state === EntityState.Charging) w.u16w(clampU16(e.castEndsAt ?? 0));
      }
      break;
    case S2C.PlayerJoined:
      w.u16w(m.id)
        .strw(m.name)
        .u16w(m.cosmetics & 0xffff);
      break;
    case S2C.PlayerLeft:
      w.u16w(m.id);
      break;
    case S2C.Pong:
      w.f64w(m.t).f64w(m.serverTime);
      break;
    case S2C.Chat:
      w.u16w(m.fromId).strw(m.text);
      break;
    case S2C.Kick:
      w.strw(m.reason);
      break;
    case S2C.Transfer:
      w.strw(m.mapId);
      writePos(w, m.spawn);
      w.i32w(Math.round(m.yaw * ANG_SCALE));
      break;
    case S2C.PartyState:
      w.u16w(m.leaderId & 0xffff).u8w(Math.min(m.members.length, 255));
      for (const p of m.members.slice(0, 255)) {
        w.u16w(p.id & 0xffff)
          .strw(p.name)
          .u8w(p.level & 0xff)
          .u32w(p.look >>> 0)
          .u16w(clampU16(p.hp))
          .u16w(clampU16(p.maxHp))
          .u8w(p.flags & 0xff);
      }
      break;
    case S2C.Events:
      w.u32w(m.tick).u16w(Math.min(m.events.length, 65535));
      for (const e of m.events.slice(0, 65535)) {
        w.u8w(e.kind);
        switch (e.kind) {
          case Ev.Damage:
            w.u16w(e.targetId)
              .u16w(e.attackerId)
              .u16w(clampU16(e.amount))
              .u16w(clampU16(e.hpAfter));
            break;
          case Ev.Heal:
            w.u16w(e.targetId).u16w(e.healerId).u16w(clampU16(e.amount)).u16w(clampU16(e.hpAfter));
            break;
          case Ev.Cast:
            w.u16w(e.casterId)
              .u8w(e.slot & 0xff)
              .u16w(clampU16(e.castMs))
              .u16w(e.targetId);
            break;
          case Ev.Death:
            w.u16w(e.victimId)
              .u16w(e.killerId)
              .u8w(e.weaponId & 0xff);
            break;
          case Ev.Respawn:
            w.u16w(e.id);
            writePos(w, e.pos);
            break;
          case Ev.Ability:
            w.u16w(e.playerId).u8w(e.slot);
            writePos(w, e.pos);
            w.u16w(e.targetId);
            break;
          case Ev.Downed:
            w.u16w(e.id);
            break;
          case Ev.Revived:
            w.u16w(e.id).u16w(e.byId);
            break;
          case Ev.MarkUnlocked:
            w.u16w(e.playerId).strw(e.markId).u8w(e.tier);
            break;
          case Ev.ContractComplete:
            w.u16w(e.playerId).strw(e.contractId);
            break;
          case Ev.QuestComplete:
            w.strw(e.questId)
              .u8w(e.chainIndex & 0xff)
              .u16w(clampU16(e.wick))
              .u16w(clampU16(e.xp));
            w.strw(e.voice).strw(e.line);
            break;
        }
      }
      break;
    case S2C.Score:
      w.u16w(Math.min(m.entries.length, 65535));
      for (const s of m.entries.slice(0, 65535)) w.u16w(s.id).u16w(s.kills).u16w(s.deaths);
      break;
    case S2C.Identity:
      w.u16w(m.playerId)
        .strw(m.name)
        .strw(m.role)
        .u32w(m.wick >>> 0)
        .u16w(m.level);
      break;
    case S2C.Loot:
      w.u16w(m.containerId);
      writeStacks(w, m.items);
      break;
    case S2C.Inventory:
      writeStacks(w, m.items);
      break;
    case S2C.Progress:
      w.u32w(m.xp >>> 0)
        .u8w(m.level)
        .u8w(m.skillPoints)
        .u8w(m.calling);
      for (const c of m.cooldownsMs) w.u16w(clampU16(c));
      w.u16w(clampU16(m.gcdMs)).u16w(clampU16(m.resource)).u16w(clampU16(m.resourceMax));
      w.u16w(clampU16(m.castMs)).u8w(m.castSlot & 0xff);
      break;
    case S2C.QuestState: {
      // One flags byte, then only the optional fields that are actually present.
      const flags =
        (m.area ? 1 : 0) |
        (m.timeLeftMs !== undefined ? 2 : 0) |
        (m.breatherMs !== undefined ? 4 : 0) |
        (m.brief ? 8 : 0) |
        (m.handInNpcId !== undefined ? 16 : 0);
      w.u8w(m.chainIndex & 0xff)
        .strw(m.questId)
        .u8w(m.kind & 0xff)
        .strw(m.title)
        .strw(m.objective);
      w.u16w(clampU16(m.progress)).u16w(clampU16(m.goal));
      w.u8w(flags);
      if (m.area) {
        writePos(w, { x: m.area.x, y: m.area.y, z: m.area.z });
        w.u16w(clampU16(m.area.radius * 10)).strw(m.area.name);
      }
      if (m.timeLeftMs !== undefined) w.u32w(Math.max(0, Math.round(m.timeLeftMs)) >>> 0);
      if (m.breatherMs !== undefined) w.u32w(Math.max(0, Math.round(m.breatherMs)) >>> 0);
      if (m.brief)
        w.strw(m.voice ?? '')
          .strw(m.brief[0])
          .strw(m.brief[1]);
      // Appended last, so every field before it keeps the offset it has always had.
      if (m.handInNpcId !== undefined) w.u16w(m.handInNpcId & 0xffff);
      break;
    }
  }
  return w.bytes();
}

const clampU16 = (v: number): number => Math.min(65535, Math.max(0, Math.round(v)));

function decodeServerUnsafe(data: ArrayBuffer | Uint8Array): ServerMessage | null {
  const r = new ByteReader(data);
  if (r.remaining < 1) return null;
  const type = r.u8r();
  switch (type) {
    case S2C.Welcome: {
      const playerId = r.u16r();
      const tick = r.u32r();
      const tickRate = r.u8r();
      const mapId = r.strr();
      const n = r.u16r();
      const players: RosterEntry[] = [];
      for (let i = 0; i < n; i++)
        players.push({ id: r.u16r(), name: r.strr(), cosmetics: r.u16r() });
      return { type, playerId, tick, tickRate, mapId, players };
    }
    case S2C.Snapshot: {
      const tick = r.u32r();
      const serverTime = r.f64r();
      const lastInputSeq = r.u32r();
      const n = r.u16r();
      const players: PlayerSnap[] = [];
      for (let i = 0; i < n; i++) {
        players.push({
          id: r.u16r(),
          pos: readPos(r),
          vel: readVel(r),
          yaw: unpackYaw(r.u8r()),
          anim: r.u8r(),
          hp: r.u16r(),
          maxHp: r.u16r(),
          flags: r.u16r(),
          look: r.u32r(),
          level: r.u8r(),
          targetId: r.u16r(),
          stamina: r.u8r(),
        });
      }
      const ne = r.u16r();
      const entities: EntitySnap[] = [];
      for (let i = 0; i < ne; i++) {
        const snap: EntitySnap = {
          id: r.u16r(),
          kind: r.u8r(),
          pos: readPos(r),
          yaw: uq16(r.i16r(), 10000),
          hp: r.u16r(),
          maxHp: r.u16r(),
          state: r.u8r(),
          targetId: r.u16r(),
          level: r.u8r(),
        };
        // Optional tail, present only while the entity is Charging (see encodeServer).
        if (snap.state === EntityState.Charging) snap.castEndsAt = r.u16r();
        entities.push(snap);
      }
      return { type, tick, serverTime, lastInputSeq, players, entities };
    }
    case S2C.PlayerJoined:
      return { type, id: r.u16r(), name: r.strr(), cosmetics: r.u16r() };
    case S2C.PlayerLeft:
      return { type, id: r.u16r() };
    case S2C.Pong:
      return { type, t: r.f64r(), serverTime: r.f64r() };
    case S2C.Chat:
      return { type, fromId: r.u16r(), text: r.strr() };
    case S2C.Kick:
      return { type, reason: r.strr() };
    case S2C.Transfer:
      return { type, mapId: r.strr(), spawn: readPos(r), yaw: r.i32r() / ANG_SCALE };
    case S2C.PartyState: {
      const leaderId = r.u16r();
      const n = r.u8r();
      const members: PartyMemberSnap[] = [];
      for (let i = 0; i < n; i++) {
        members.push({
          id: r.u16r(),
          name: r.strr(),
          level: r.u8r(),
          look: r.u32r(),
          hp: r.u16r(),
          maxHp: r.u16r(),
          flags: r.u8r(),
        });
      }
      return { type, leaderId, members };
    }
    case S2C.Events: {
      const tick = r.u32r();
      const n = r.u16r();
      const events: GameEvent[] = [];
      for (let i = 0; i < n; i++) {
        const kind = r.u8r();
        switch (kind) {
          case Ev.Damage:
            events.push({
              kind,
              targetId: r.u16r(),
              attackerId: r.u16r(),
              amount: r.u16r(),
              hpAfter: r.u16r(),
            });
            break;
          case Ev.Heal:
            events.push({
              kind,
              targetId: r.u16r(),
              healerId: r.u16r(),
              amount: r.u16r(),
              hpAfter: r.u16r(),
            });
            break;
          case Ev.Cast:
            events.push({
              kind,
              casterId: r.u16r(),
              slot: r.u8r(),
              castMs: r.u16r(),
              targetId: r.u16r(),
            });
            break;
          case Ev.Death:
            events.push({ kind, victimId: r.u16r(), killerId: r.u16r(), weaponId: r.u8r() });
            break;
          case Ev.Respawn:
            events.push({ kind, id: r.u16r(), pos: readPos(r) });
            break;
          case Ev.Ability:
            events.push({
              kind,
              playerId: r.u16r(),
              slot: r.u8r(),
              pos: readPos(r),
              targetId: r.u16r(),
            });
            break;
          case Ev.Downed:
            events.push({ kind, id: r.u16r() });
            break;
          case Ev.Revived:
            events.push({ kind, id: r.u16r(), byId: r.u16r() });
            break;
          case Ev.MarkUnlocked:
            events.push({ kind, playerId: r.u16r(), markId: r.strr(), tier: r.u8r() });
            break;
          case Ev.ContractComplete:
            events.push({ kind, playerId: r.u16r(), contractId: r.strr() });
            break;
          case Ev.QuestComplete:
            events.push({
              kind,
              questId: r.strr(),
              chainIndex: r.u8r(),
              wick: r.u16r(),
              xp: r.u16r(),
              voice: r.strr(),
              line: r.strr(),
            });
            break;
          default:
            return null;
        }
      }
      return { type, tick, events };
    }
    case S2C.Score: {
      const n = r.u16r();
      const entries: ScoreEntry[] = [];
      for (let i = 0; i < n; i++) entries.push({ id: r.u16r(), kills: r.u16r(), deaths: r.u16r() });
      return { type, entries };
    }
    case S2C.Identity:
      return {
        type,
        playerId: r.u16r(),
        name: r.strr(),
        role: r.strr(),
        wick: r.u32r(),
        level: r.u16r(),
      };
    case S2C.Loot:
      return { type, containerId: r.u16r(), items: readStacks(r) };
    case S2C.Inventory:
      return { type, items: readStacks(r) };
    case S2C.Progress: {
      const xp = r.u32r();
      const level = r.u8r();
      const skillPoints = r.u8r();
      const calling = r.u8r();
      const cooldownsMs: [number, number, number, number, number, number] = [
        r.u16r(),
        r.u16r(),
        r.u16r(),
        r.u16r(),
        r.u16r(),
        r.u16r(),
      ];
      const gcdMs = r.u16r();
      const resource = r.u16r();
      const resourceMax = r.u16r();
      const castMs = r.u16r();
      const castSlot = r.u8r();
      return {
        type,
        xp,
        level,
        skillPoints,
        calling,
        cooldownsMs,
        gcdMs,
        resource,
        resourceMax,
        castMs,
        castSlot,
      };
    }
    case S2C.QuestState: {
      const chainIndex = r.u8r();
      const questId = r.strr();
      const kind = r.u8r();
      const title = r.strr();
      const objective = r.strr();
      const progress = r.u16r();
      const goal = r.u16r();
      const flags = r.u8r();
      // exactOptionalPropertyTypes: the optional fields are spread in only when actually present.
      const area =
        (flags & 1) === 0 ? undefined : { ...readPos(r), radius: r.u16r() / 10, name: r.strr() };
      const timeLeftMs = (flags & 2) === 0 ? undefined : r.u32r();
      const breatherMs = (flags & 4) === 0 ? undefined : r.u32r();
      const voice = (flags & 8) === 0 ? undefined : r.strr();
      const brief: [string, string] | undefined =
        (flags & 8) === 0 ? undefined : [r.strr(), r.strr()];
      const handInNpcId = (flags & 16) === 0 ? undefined : r.u16r();
      return {
        type,
        chainIndex,
        questId,
        kind,
        title,
        objective,
        progress,
        goal,
        ...(area ? { area } : {}),
        ...(timeLeftMs !== undefined ? { timeLeftMs } : {}),
        ...(breatherMs !== undefined ? { breatherMs } : {}),
        ...(voice !== undefined ? { voice } : {}),
        ...(brief !== undefined ? { brief } : {}),
        ...(handInNpcId !== undefined ? { handInNpcId } : {}),
      };
    }
    default:
      return null;
  }
}

/** Safe decoders: malformed or truncated packets yield null instead of throwing. */
export function decodeClient(data: ArrayBuffer | Uint8Array): ClientMessage | null {
  try {
    return decodeClientUnsafe(data);
  } catch {
    return null;
  }
}
export function decodeServer(data: ArrayBuffer | Uint8Array): ServerMessage | null {
  try {
    return decodeServerUnsafe(data);
  } catch {
    return null;
  }
}

/**
 * Round an input command to exactly the precision the wire format carries, so the client's
 * prediction and the server's simulation step identical numbers. Client and bots MUST run
 * every command through this before `stepPlayer` and before sending it.
 */
export function quantizeInputCmd(c: InputCmd): InputCmd {
  const move = (v: number) => Math.max(-127, Math.min(127, Math.round(v * 127))) / 127;
  return {
    seq: c.seq >>> 0,
    dt: Math.max(0, Math.min(65535, Math.round(c.dt * 10000))) / 10000,
    moveX: move(c.moveX),
    moveY: move(c.moveY),
    yaw: Math.round(c.yaw * ANG_SCALE) / ANG_SCALE,
    buttons: c.buttons & 0xffff,
    targetId: c.targetId & 0xffff,
  };
}
